import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Input,
  Select,
  Table,
  Tag,
} from '@surecompanies/core_components'
import { DownOutlined } from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { browserHistory } from 'react-router'
import dayjs from 'dayjs'
import Page from 'layouts/Page'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import ClientSelect from 'components/ClientSelect'
import { useMyClientsQuery } from 'enrollment-platform/api/persons'
import CensusQueueGrid from '../../../../../CensusManagement/components/CensusQueueGrid'
import { CensusUploadJobChannel } from '../../../../../CensusManagement/api/dto/census-upload-job.dto'
import { HRIS_PATH } from '../../../../constants'
import { getCensusRuns } from './census-run-api'
import {
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  QueryValue,
  readCensusRunUrl,
  writeCensusRunUrl,
} from './census-run-url'
import ExpandedRunDetails from './ExpandedRunDetails'
import { CensusRunDto, CensusRunStatus } from './types'
import style from './CensusSyncVisibility.module.scss'

const STATUS_TAG: Record<CensusRunStatus, { color: string; label: string }> = {
  COMPLETED: { color: 'success', label: 'Complete' },
  RUNNING: { color: 'warning', label: 'In progress' },
  FAILED: { color: 'error', label: 'Failed' },
  BLOCKED: { color: 'error', label: 'Blocked' },
  AWAITING_ACTION: { color: 'warning', label: 'Awaiting action' },
}

const formatTime = (value?: string): string =>
  value ? dayjs(value).format('M/D/YYYY - h:mm:ssA') : '—'

/** How a run's channel reads on screen. The stored code is short for a column
 *  header but means nothing to a reader. */
const CHANNEL_LABELS: Record<string, string> = {
  API: 'API Integration',
  AMA: 'AMA',
  MANUAL_UPLOAD: 'Manual upload',
}

const columns = [
  {
    title: 'Customer',
    dataIndex: 'clientCompanyName',
    width: 180,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: 140,
    render: (status: CensusRunStatus, row: CensusRunDto) => {
      const tag = STATUS_TAG[status]
      // The reason is a whole sentence, so it rides on the tag rather than
      // widening the column for every row.
      return (
        <Tag color={tag?.color} title={row.failureReason}>
          {tag?.label ?? status}
        </Tag>
      )
    },
  },
  {
    title: 'Channel',
    dataIndex: 'channel',
    width: 150,
    render: (value: string) => (
      <Tag color={value === 'AMA' ? 'blue' : 'default'}>
        {CHANNEL_LABELS[value] ?? value}
      </Tag>
    ),
  },
  {
    title: 'File',
    dataIndex: 'sourceFileName',
    width: 380,
    render: (value?: string) =>
      value ? (
        <span title={value} style={{ fontFamily: 'monospace', fontSize: 12.5 }}>
          {value}
        </span>
      ) : (
        '—'
      ),
  },
  {
    title: 'Time Received',
    dataIndex: 'startedAt',
    width: 200,
    render: (value: string) => formatTime(value),
  },
  {
    title: 'Time Processed',
    dataIndex: 'endedAt',
    width: 200,
    render: (value?: string) => formatTime(value),
  },
  {
    title: 'Original Data',
    dataIndex: 'rowCount',
    width: 130,
    render: (value?: number) =>
      value === undefined || value === null ? '—' : `${value} records`,
  },
  {
    title: 'Import ID',
    dataIndex: 'importPublicKey',
    width: 140,
    render: (value?: string) =>
      value ? (
        <span style={{ fontFamily: 'monospace', fontSize: 12.5 }}>{value}</span>
      ) : (
        '—'
      ),
  },
]

/** The channels a run can come from. A run always has exactly one. */
const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'API', label: 'API Integration' },
  { value: 'AMA', label: 'AMA' },
]

// The queued jobs of the channels this page covers; the table below lists the runs they produce
const ALL_QUEUE_CHANNELS: CensusUploadJobChannel[] = ['API', 'AMA']
const QUEUE_CHANNELS_BY_FILTER: Record<string, CensusUploadJobChannel[]> = {
  API: ['API'],
  AMA: ['AMA'],
}

function CensusSyncVisibility() {
  const queryClient = useQueryClient()
  // The URL is the starting point, so a link someone was sent opens on what
  // they were looking at rather than on an empty list.
  const initial = readCensusRunUrl(
    browserHistory.getCurrentLocation().query as Record<string, QueryValue>
  )
  const [selectedClientId, setSelectedClientId] = useState(
    initial.clientPublicKey
  )
  const [channel, setChannel] = useState(initial.channel)
  const [firstName, setFirstName] = useState(initial.firstName)
  const [lastName, setLastName] = useState(initial.lastName)
  // Typing a name should not fire a query per keystroke — the search hits an
  // index of every person in every run.
  const [nameFilter, setNameFilter] = useState({
    firstName: initial.firstName,
    lastName: initial.lastName,
  })
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>(
    initial.runId === '' ? [] : [initial.runId]
  )
  const [page, setPage] = useState(initial.page)
  const [limit, setLimit] = useState(initial.limit)

  // Skipped on mount: the effect would otherwise reset the page a shared link
  // asked for, dropping ?page= from the URL and sending the reader back to the
  // first page of a list they did not ask to start over.
  const hasTypedName = useRef(false)

  useEffect(() => {
    if (!hasTypedName.current) {
      hasTypedName.current = true
      return
    }
    const timer = setTimeout(() => {
      setNameFilter({ firstName, lastName })
      setPage(DEFAULT_PAGE)
    }, 400)
    return () => clearTimeout(timer)
  }, [firstName, lastName])

  // Written after the debounce, so the URL follows what the list is actually
  // showing rather than what is half-typed.
  useEffect(() => {
    writeCensusRunUrl({
      clientPublicKey: selectedClientId,
      channel,
      firstName: nameFilter.firstName,
      lastName: nameFilter.lastName,
      page,
      limit,
      runId: expandedRowKeys[0] ?? '',
    })
  }, [
    selectedClientId,
    channel,
    nameFilter.firstName,
    nameFilter.lastName,
    page,
    limit,
    expandedRowKeys,
  ])

  const { data: clients = [], isLoading: clientsLoading } = useMyClientsQuery()
  const companyList = useMemo(
    () => clients.map((c) => ({ id: c.id, companyName: c.name })),
    [clients]
  )
  // A client can arrive from the URL before the company list does. Without a
  // stand-in the select would show its raw id, or fall back to "All Companies"
  // while the list is in fact filtered to one client.
  const companyOptions =
    clientsLoading && selectedClientId !== ''
      ? [{ id: selectedClientId, companyName: 'Loading…' }]
      : companyList

  const { data, isPending, isError } = useQuery({
    queryKey: [
      'census-runs',
      page,
      limit,
      selectedClientId,
      channel,
      nameFilter.firstName,
      nameFilter.lastName,
    ],
    queryFn: () =>
      getCensusRuns({
        page,
        limit,
        channel: channel || undefined,
        clientPublicKey: selectedClientId || undefined,
        firstName: nameFilter.firstName || undefined,
        lastName: nameFilter.lastName || undefined,
      }),
    staleTime: 60 * 1000,
  })

  const clientNameLookup = useMemo(
    () =>
      clients.reduce<Record<string, string>>((lookup, client) => {
        lookup[client.id] = client.name
        return lookup
      }, {}),
    [clients]
  )
  const queueFilter = useMemo(
    () => ({ selectedClient: clients.find((c) => c.id === selectedClientId) }),
    [clients, selectedClientId]
  )
  const refreshRuns = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['census-runs'] }),
    [queryClient]
  )

  const handleClientChange = (value: string) => {
    setSelectedClientId(value)
    setPage(DEFAULT_PAGE)
  }

  // The whole row expands, not just the caret. The caret keeps its own handler
  // so it stays the obvious target, and both go through this.
  const toggleRow = (runId: string) => {
    setExpandedRowKeys((keys) =>
      keys.includes(runId)
        ? keys.filter((key) => key !== runId)
        : [...keys, runId]
    )
  }

  const handleChannelChange = (value: string) => {
    setChannel(value)
    setPage(DEFAULT_PAGE)
  }

  const isFiltered =
    selectedClientId !== '' ||
    channel !== '' ||
    firstName !== '' ||
    lastName !== ''

  const handleClearAll = () => {
    setSelectedClientId('')
    setChannel('')
    setFirstName('')
    setLastName('')
    // The name inputs are debounced, so clear what the query reads as well
    // rather than waiting out the timer.
    setNameFilter({ firstName: '', lastName: '' })
    setPage(DEFAULT_PAGE)
    setLimit(DEFAULT_LIMIT)
    setExpandedRowKeys([])
  }

  return (
    <Page
      title="Census Sync Visibility"
      maxWidth="100%"
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: `/` },
            { title: 'HRIS', path: HRIS_PATH },
            { title: 'Census Sync Visibility' },
          ]}
        />
      }
    >
      <p style={{ maxWidth: 620, marginBottom: 24 }}>
        Expand a file to see how it moved through the processing pipeline.
      </p>

      <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
        <ClientSelect
          allCompaniesPlacholder={{
            id: '',
            companyName: clientsLoading
              ? 'Loading companies…'
              : 'All Companies',
          }}
          companies={companyOptions}
          defaultCompanyId={''}
          selectedCompanyId={selectedClientId}
          onChange={handleClientChange}
          disabled={clientsLoading}
          style={{ width: 240 }}
        />
        <Select
          value={channel}
          onChange={handleChannelChange}
          options={CHANNEL_OPTIONS}
          style={{ width: 180 }}
        />
        <Input
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          style={{ width: 180 }}
          allowClear
        />
        <Input
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          style={{ width: 180 }}
          allowClear
        />
        <Button onClick={handleClearAll} disabled={!isFiltered}>
          Clear all
        </Button>
      </div>
      {(nameFilter.firstName || nameFilter.lastName) && (
        <p style={{ marginTop: -8, marginBottom: 16, color: '#6b7280' }}>
          Showing runs that mention this person, including rows the run dropped.
          Runs from before this search existed are not indexed.
        </p>
      )}

      <CensusQueueGrid
        clientNameLookup={clientNameLookup}
        refreshKey={false}
        filter={queueFilter}
        channel={QUEUE_CHANNELS_BY_FILTER[channel] ?? ALL_QUEUE_CHANNELS}
        onJobsFinished={refreshRuns}
        showAction
        showChannel
      />

      <Table
        dataSource={data?.data ?? []}
        columns={columns as any}
        rowKey={(row: CensusRunDto) => row.id}
        scroll={{ x: 'max-content' }}
        loading={isPending}
        locale={{
          emptyText: isError
            ? 'Could not load census runs.'
            : 'No census runs yet for this client.',
        }}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.meta?.total ?? 0,
          showSizeChanger: true,
          onChange: (nextPage: number, nextSize?: number) => {
            setPage(nextPage)
            if (nextSize) setLimit(nextSize)
          },
        }}
        expandable={{
          expandedRowKeys,
          expandedRowClassName: () => style.expandedRow,
          // antd types a key as string | number; every key here is a run id.
          onExpandedRowsChange: (keys: readonly React.Key[]) =>
            setExpandedRowKeys(keys.map(String)),
          expandedRowRender: (row: CensusRunDto) => (
            <ExpandedRunDetails
              runId={row.id}
              channel={row.channel}
              firstName={nameFilter.firstName}
              lastName={nameFilter.lastName}
              clientPublicKey={row.clientPublicKey}
              clientCompanyName={row.clientCompanyName}
            />
          ),
          // The caret sits inside the row, so its click would bubble to onRow and
          // toggle a second time, leaving the row exactly as it was. It stops
          // the event and goes through the same toggle.
          expandIcon: ({ expanded, record }: any) => (
            <DownOutlined
              onClick={(e) => {
                e.stopPropagation()
                toggleRow(record.id)
              }}
              style={{
                cursor: 'pointer',
                transition: 'transform 160ms ease',
                transform: expanded ? 'rotate(180deg)' : 'none',
              }}
            />
          ),
        }}
        onRow={(row: CensusRunDto) => ({
          onClick: () => toggleRow(row.id),
          style: {
            cursor: 'pointer',
            // An expanded run keeps its own tint so the row and the panel below
            // read as one block.
            background: expandedRowKeys.includes(row.id)
              ? '#eaf2ef'
              : undefined,
          },
        })}
      />
    </Page>
  )
}

export default CensusSyncVisibility
